/**
 * Org events (PRD §5.5 Phase 2): org admins author events and choose reach
 * per event via the visibility ladder — internal (org members' calendars) →
 * shared (the org's public calendar share links) → public (the global feed:
 * citizen app + public site). Direct publish; system admins hold takedown.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CalendarPlus } from 'lucide-react';
import api from '../../lib/api';
import AddressAutocomplete from '../AddressAutocomplete';
import Button from '@shared/ui/Button';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
} from '../StandardTable';
import { EditIcon, DeleteIcon } from '../icons';
import ActionsHeaderHelp from '../ActionsHeaderHelp';
import TimeComboBox, { addMinutes } from '../calendar/TimeComboBox';
import { HelpTrigger } from '../../help';
import ConfirmationDialog from '../ConfirmationDialog';

interface OrgEvent {
  id: string;
  title: string;
  description: string | null;
  event_type: string | null;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  format: string;
  venue: string | null;
  address: string | null;
  city: string | null;
  visibility: 'internal' | 'shared' | 'public';
  host_name: string | null;
  link_url: string | null;
  registration_url: string | null;
}

const EVENT_TYPES = ['town_hall', 'party_meeting', 'convention_assembly', 'campaign_event',
  'volunteer_action', 'fundraiser', 'debate_forum', 'rally_protest', 'advocacy_briefing',
  'training', 'social', 'other'];

const VISIBILITY_HELP: Record<string, string> = {
  internal: 'Only members of your organization see it.',
  shared: 'Also appears on your organization’s shared public calendar links.',
  public: 'Also published to the public events feed — the citizen app and the public site.',
};

const VISIBILITY_TONE: Record<string, string> = {
  internal: 'bg-neutral-100 text-slate-600 dark:bg-background-dark-tertiary dark:text-text-dark-secondary',
  shared: 'bg-info-50 text-info-700 dark:bg-info-900/20 dark:text-info-300',
  public: 'bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-300',
};

const EMPTY_FORM = {
  title: '', event_date: '', start_time: '', end_time: '', event_type: '',
  format: 'in_person', venue: '', address: '', city: '', description: '',
  link_url: '', registration_url: '', visibility: 'internal',
};

const inputClasses =
  'w-full rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-tertiary px-3 py-1.5 text-sm text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClasses = 'block text-xs font-semibold text-slate-600 dark:text-text-dark-secondary mb-1';
const cell = 'px-4 py-2 text-sm text-text-primary dark:text-text-dark-primary';

const OrgEventsTab: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OrgEvent | null>(null);
  const [pendingDelete, setPendingDelete] = useState<OrgEvent | null>(null);
  const [form, setForm] = useState<Record<string, string>>(EMPTY_FORM);
  const queryClient = useQueryClient();

  const events = useQuery({
    queryKey: ['org-events', organizationId],
    queryFn: async () => {
      const response = await api.get(`/organizations/${organizationId}/events`);
      return (response.data?.data ?? []) as OrgEvent[];
    },
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (event: OrgEvent) => {
    setEditing(event);
    setForm({
      title: event.title,
      event_date: String(event.event_date).slice(0, 10),
      start_time: event.start_time ? String(event.start_time).slice(0, 5) : '',
      end_time: event.end_time ? String(event.end_time).slice(0, 5) : '',
      event_type: event.event_type ?? '',
      format: event.format !== 'unknown' ? event.format : 'in_person',
      venue: event.venue ?? '', address: event.address ?? '', city: event.city ?? '',
      description: event.description ?? '',
      link_url: event.link_url ?? '', registration_url: event.registration_url ?? '',
      visibility: event.visibility,
    });
    setModalOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = { ...form, event_type: form.event_type || undefined };
      return editing
        ? api.put(`/organizations/${organizationId}/events/${editing.id}`, payload)
        : api.post(`/organizations/${organizationId}/events`, payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Event updated' : 'Event created');
      setModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['org-events', organizationId] });
      // Public org events appear on the Pro home calendar.
      queryClient.invalidateQueries({ queryKey: ['proHome', 'calendar'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to save event'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/organizations/${organizationId}/events/${id}`),
    onSuccess: () => {
      toast.success('Event deleted');
      queryClient.invalidateQueries({ queryKey: ['org-events', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['proHome', 'calendar'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to delete event'),
  });

  const set = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const setStartTime = (newStart: string) => {
    setForm((current) => {
      if (!newStart) return { ...current, start_time: '' };
      let end = current.end_time;
      if (current.start_time && end) {
        const duration =
          (parseInt(end.slice(0, 2), 10) * 60 + parseInt(end.slice(3, 5), 10)) -
          (parseInt(current.start_time.slice(0, 2), 10) * 60 + parseInt(current.start_time.slice(3, 5), 10));
        end = addMinutes(newStart, duration > 0 ? duration : 60);
      } else {
        end = addMinutes(newStart, 60);
      }
      return { ...current, start_time: newStart, end_time: end };
    });
  };

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            Organization Events
            <HelpTrigger contentKey="org-events" />
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Events your organization hosts. Visibility controls reach: internal → shared calendar links → the public feed.
          </p>
        </div>
        <Button variant="primary" size="sm" icon={CalendarPlus} onClick={openCreate}>Add Event</Button>
      </div>

      <StandardTable loading={events.isLoading} horizontalScroll>
        <StandardTableHeader>
          <tr>
            <StandardTableHeaderCell>Date</StandardTableHeaderCell>
            <StandardTableHeaderCell>Title</StandardTableHeaderCell>
            <StandardTableHeaderCell>Type</StandardTableHeaderCell>
            <StandardTableHeaderCell>Visibility</StandardTableHeaderCell>
            <StandardTableHeaderCell>
              <span className="inline-flex items-center gap-1.5">
                Actions
                <ActionsHeaderHelp icons={['edit', 'delete']} />
              </span>
            </StandardTableHeaderCell>
          </tr>
        </StandardTableHeader>
        <tbody>
          {(events.data ?? []).map((event) => (
            <tr key={event.id} className="border-b border-border-light dark:border-border-dark-hairline">
              <td className={cell}>{String(event.event_date).slice(0, 10)}</td>
              <td className={cell}>{event.title}</td>
              <td className={cell}>{event.event_type ?? '—'}</td>
              <td className={cell}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${VISIBILITY_TONE[event.visibility]}`}>
                  {event.visibility}
                </span>
              </td>
              <td className={cell}>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel="Edit event" title="Edit event" onClick={() => openEdit(event)} />
                  <Button variant="ghost" size="sm" icon={DeleteIcon} ariaLabel="Delete event" title="Delete event"
                    onClick={() => setPendingDelete(event)} />
                </div>
              </td>
            </tr>
          ))}
          {!events.isLoading && (events.data ?? []).length === 0 && (
            <tr><td className={cell} colSpan={5}>No events yet — add your first event to put it on your members&apos; calendars.</td></tr>
          )}
        </tbody>
      </StandardTable>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg rounded-lg bg-white dark:bg-background-dark-secondary shadow-xl flex flex-col max-h-[90vh]">
            <div className="border-b border-border-light dark:border-border-dark-hairline px-5 py-3.5">
              <h2 className="font-display font-semibold text-[15px] text-primary-800 dark:text-text-dark-primary">
                {editing ? 'Edit Event' : 'Add Event'}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div>
                <label htmlFor="oe-title" className={labelClasses}>Title *</label>
                <input id="oe-title" className={inputClasses} value={form.title} onChange={set('title')} maxLength={200} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor="oe-date" className={labelClasses}>Date *</label>
                  <input id="oe-date" type="date" className={inputClasses} value={form.event_date} onChange={set('event_date')} />
                </div>
                <div>
                  <label htmlFor="oe-start" className={labelClasses}>Start</label>
                  <TimeComboBox id="oe-start" value={form.start_time} onChange={setStartTime} />
                </div>
                <div>
                  <label htmlFor="oe-end" className={labelClasses}>End</label>
                  <TimeComboBox id="oe-end" value={form.end_time} baseTime={form.start_time || undefined}
                    onChange={(v) => setForm((c) => ({ ...c, end_time: v }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="oe-type" className={labelClasses}>Type</label>
                  <select id="oe-type" className={inputClasses} value={form.event_type} onChange={set('event_type')}>
                    <option value="">—</option>
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="oe-format" className={labelClasses}>Format</label>
                  <select id="oe-format" className={inputClasses} value={form.format} onChange={set('format')}>
                    <option value="in_person">In person</option>
                    <option value="virtual">Virtual</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
              {form.format !== 'virtual' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="oe-venue" className={labelClasses}>Venue</label>
                    <input id="oe-venue" className={inputClasses} value={form.venue} onChange={set('venue')} maxLength={255} />
                  </div>
                  <div>
                    <label htmlFor="oe-city" className={labelClasses}>City</label>
                    <input id="oe-city" className={inputClasses} value={form.city} onChange={set('city')} maxLength={100} />
                  </div>
                  <div className="col-span-2">
                    <label htmlFor="oe-address" className={labelClasses}>Address</label>
                    {/* Google Places suggestions; picking one fills City too. */}
                <AddressAutocomplete
                  id="oe-address"
                  inputClassName={inputClasses}
                  showIcon={false}
                  placeholder=""
                  value={form.address}
                  onChange={(value) => setForm((current) => ({ ...current, address: value.slice(0, 255) }))}
                  onSelect={(parts) => setForm((current) => ({ ...current, city: parts.city || current.city }))}
                />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="oe-link" className={labelClasses}>Event link</label>
                  <input id="oe-link" type="url" className={inputClasses} value={form.link_url} onChange={set('link_url')} placeholder="https://…" />
                </div>
                <div>
                  <label htmlFor="oe-rsvp" className={labelClasses}>RSVP / registration link</label>
                  <input id="oe-rsvp" type="url" className={inputClasses} value={form.registration_url} onChange={set('registration_url')} placeholder="https://…" />
                </div>
              </div>
              <div>
                <label htmlFor="oe-desc" className={labelClasses}>Description</label>
                <textarea id="oe-desc" rows={3} className={inputClasses} value={form.description} onChange={set('description')} maxLength={2000} />
              </div>
              <div>
                <label htmlFor="oe-vis" className={labelClasses}>Visibility</label>
                <select id="oe-vis" className={inputClasses} value={form.visibility} onChange={set('visibility')}>
                  <option value="internal">Internal</option>
                  <option value="shared">Shared — on your public calendar links</option>
                  <option value="public">Public — citizen app + public site</option>
                </select>
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {VISIBILITY_HELP[form.visibility]}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border-light dark:border-border-dark-hairline px-5 py-3">
              <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button variant="primary" size="sm" loading={save.isLoading}
                disabled={!form.title.trim() || !form.event_date} onClick={() => save.mutate()}>
                {editing ? 'Save Changes' : 'Add Event'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmationDialog
        isOpen={pendingDelete !== null}
        title="Delete event"
        message={pendingDelete ? `Delete "${pendingDelete.title}"? It will be removed from your members' calendars.` : ''}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default OrgEventsTab;
